/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import { useQuery } from '@apollo/client'

import { findAllRewardItemsLog, findAllRewardItemsLogVariables } from '__generatedTypes__/findAllRewardItemsLog'
import { findOneUser_findOneUser as User } from '__generatedTypes__/findOneUser'
import Loader from 'components/Loader'
import { GET_REWARD_ITEMS_LOG } from 'queries/users'
import Table from 'components/Table'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { useAuthContext } from 'utils/auth'
import * as tabsClasses from '../styles'
import * as classes from './styles'
import { ItemsManagement } from './ItemsManagement'
import { useItems } from './useItems'

type Props = {
  user: User
}

export const Items: FC<Props> = ({ user }) => {
  const { isAdmin } = useAuthContext()
  const { items, itemsLoading, refetchItems, platforms, platformsLoading, selectedPlatform, setSelectedPlatform } =
    useItems(user.id)

  const {
    data: tokensData,
    loading: tokensLoading,
    refetch: refetchTokenLogs,
  } = useQuery<findAllRewardItemsLog, findAllRewardItemsLogVariables>(GET_REWARD_ITEMS_LOG, {
    variables: {
      query: {
        take: TAKE_ALL_ITEMS_COUNT,
        where: {
          user: {
            id: user.id,
          },
        },
      },
    },
  })

  const pauseTokensLogItems = tokensData?.findAllRewardItemsLog?.items

  const onSuccessfullUpdate = async () => {
    await Promise.all([refetchItems(), refetchTokenLogs()])
  }

  const renderItemLogsTable = () => {
    if (tokensLoading) {
      return <Loader />
    }

    if (!pauseTokensLogItems?.length) {
      return <Typography data-testid="no-items-logs">No item logs found</Typography>
    }

    return (
      <Table
        css={classes.itemLogsTable}
        hideHeader
        columns={[
          {
            title: 'Log',
            renderCell: (pauseTokenLog) => <span>{pauseTokenLog.log}</span>,
            flex: 1,
          },
        ]}
        items={pauseTokensLogItems}
        wrapperMinWidth={350}
        data-testid="items-logs"
      />
    )
  }

  if (platformsLoading || !selectedPlatform) {
    return <Loader />
  }

  return (
    <section css={tabsClasses.container}>
      <div css={tabsClasses.form}>
        <Typography variant="title3" css={tabsClasses.title}>
          Items
        </Typography>

        <Divider />

        <Typography variant="title3" css={[tabsClasses.title, classes.secondaryTitle]}>
          Item Management
        </Typography>

        <ItemsManagement
          user={user}
          itemsLoading={itemsLoading}
          items={items}
          platforms={platforms}
          selectedPlatform={selectedPlatform}
          setSelectedPlatform={setSelectedPlatform}
          onSuccessfullSubmit={onSuccessfullUpdate}
          isReadOnly={!isAdmin}
        />

        <Typography variant="title3" css={[tabsClasses.title, classes.secondaryTitle]}>
          Item Logs
        </Typography>
        {renderItemLogsTable()}
      </div>
    </section>
  )
}
